import { useState, useContext, useRef } from 'react';
import { AttachFile, Check } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButton,
    SttTable,
    SttTopControls,
} from '../../../sporttia/all';
import { useCrud, useInteractionsFiles } from '../../../lib/hooks';
import ModalFile from '../events/ModalFile';
import DateBuilder from '../../../lib/DateBuilder';

type Props = {
    activity: Activity;
};

type Activity = {
    id: number;
    name?: string;
    poster?: {
        id: number;
    };
};

type File = {
    id: number;
    name: string;
    description?: string;
    public?: boolean;
    format?: string;
    url?: string;
    created?: string;
};

type FilesResponse = {
    count: number;
    rows: File[];
};

export default function ActivityDocuments({
    activity,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { uploadFile, downloadFile } = useInteractionsFiles();
    const inputFile = useRef<HTMLInputElement>(null);
    const [_uploading, setUploading] = useState(false);
    const [_activity, setActivity] = useState<Activity>(activity);
    const [files, setFiles, , Get, _Post, Put, Delete] =
        useCrud<FilesResponse>();
    const [file, setFile] = useState<File>();
    const [forceUpdate, setforceUpdate] = useState(false);

    function loadFiles(): void {
        Get(`/activities/${_activity.id}/files`)
            .then((response: unknown) => {
                const data = response as FilesResponse;
                if (data.rows) {
                    setFiles(data);
                }
            })
            .catch(console.log);
    }

    function openSelectFile(): void {
        inputFile.current?.click();
    }

    function onFileSelected(files: FileList | null): void {
        if (files && files.length > 0) {
            const file = files[0]!;

            setUploading(true);

            // V7 API expects 'name' field and 'idActivity' (format is extracted from file.mimetype on backend)
            uploadFile(
                {
                    name: file.name,
                    type: 'ACTIVITY_DOC',
                    idActivity: _activity.id,
                },
                file,
            )
                .then(() => {
                    setUploading(false);
                    cxt?.showMessage('S', t('Uploaded'));
                    loadFiles();
                })
                .catch(console.log);
        }
    }

    function saveFile(file: File): void {
        const { name, description } = file;
        const isPublic = file.public;

        Put(`/files/${file.id}`, { name, description, public: isPublic })
            .then((response: unknown) => {
                const data = response as { file?: File };
                if (data.file) {
                    setFiles({
                        ...files,
                        rows: files.rows.map((f: any) =>
                            f.id === file.id ? data.file! : f,
                        ),
                    });
                    setFile(undefined);
                }
            })
            .catch(console.log);
    }

    function deleteFile(file: File): void {
        Delete(`/files/${file.id}`).then((_response: any) => {
            setFiles({
                count: files.count - 1,
                rows: files.rows.filter((f: any) => f.id !== file.id),
            });
            setFile(undefined);
        });
    }

    function downloadFiles(file: File): void {
        const parts = file.name.split('.');
        const extension = parts[parts.length - 1] || '';

        if (file.hasOwnProperty('url') && file.url !== '') {
            downloadFile(file.url!, file.format, {}, file.name);
        } else if (extension in ['jpg', 'jpeg', 'png']) {
            downloadFile(
                `/images/${file.id}.${extension}`,
                file.format,
                {},
                file.name,
            );
        } else {
            downloadFile(
                `/files/${file.id}.${extension}`,
                file.format,
                {},
                file.name,
            );
        }
    }

    function setPoster(file: File): void {
        Put(`/activities/${_activity.id}`, { idPoster: file.id })
            .then((response: unknown) => {
                const data = response as { activity?: Activity };
                if (data.activity) {
                    setActivity(data.activity);
                    setforceUpdate(!forceUpdate);
                }
            })
            .catch(console.log);
    }

    return (
        <>
            <input
                type="file"
                id="file"
                ref={inputFile}
                style={{ display: 'none' }}
                onChange={(_event) => onFileSelected(_event.target.files)}
            />
            <SttTopControls
                mainAction={{
                    type: 'component',
                    component: (
                        <SttButton
                            caption={t('Add')}
                            variant="contained"
                            color="primary"
                            startIcon={<AttachFile />}
                            onClick={openSelectFile}
                        />
                    ),
                }}
            />
            <SttTable
                data={files}
                onFetch={loadFiles}
                forceUpdateSwitch={forceUpdate}
                columns={[
                    {
                        title: t('Created'),
                        field: 'created',
                        align: 'left',
                        value: (row: File) =>
                            new DateBuilder(
                                row?.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang?.key,
                            ).dmy(),
                    },
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        onClick: (row: File) => setFile(row),
                        align: 'left',
                    },
                    {
                        align: 'right',
                        value: (row: File) => {
                            if (
                                _activity.poster &&
                                _activity.poster.id === row.id
                            ) {
                                return (
                                    <Box
                                        display="inline-flex"
                                        alignItems="center"
                                    >
                                        <span style={{ marginRight: 8 }}>
                                            {t('pages.events.poster')}
                                        </span>
                                        <Check style={{ fontSize: 15 }} />
                                    </Box>
                                );
                            }
                            return (
                                <a href="#" onClick={() => setPoster(row)}>
                                    {t('pages.events.selectPoster')}
                                </a>
                            );
                        },
                    },
                ]}
            />
            {file && (
                <ModalFile
                    onClose={() => setFile(undefined)}
                    item={file as any}
                    onSave={(f) => saveFile(f as File)}
                    onDelete={(f) => deleteFile(f as File)}
                    onDownload={(f) => downloadFiles(f as File)}
                />
            )}
        </>
    );
}
